<template>
  <div v-if="view?.state !== 4" class="activity-display-pool" role="region" aria-label="活动奖池">
    <img v-if="background" class="display-pool-background" :src="background" aria-hidden="true" />
    <div class="display-pool-heading"><h3>{{ pool.title }}</h3><span>{{ view?.label || '暂不可用' }}</span></div>
    <div class="display-pool-amount">
      <img v-if="amountBackground" class="display-pool-background" :src="amountBackground" aria-hidden="true" />
      <span class="display-pool-number" :style="{ backgroundColor: amountBackdrop, color: fontColor }" :aria-label="view ? `${view.amount} 元` : '奖池暂不可用'"><span v-for="(character, index) in view?.amount || '—'" :key="index" :style="{ color: /[,.]/.test(character) ? separatorColor : fontColor }">{{ character }}</span><small v-if="view?.state !== 4"> 元</small></span>
    </div>
    <span class="display-pool-note">展示金额，实际奖励以闯关格子为准。</span>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { displayPoolView, displayPoolImage, displayPoolColor } from '../../services/activities/activityPoolDisplay.mjs';

const props = defineProps({ pool: { type: Object, required: true }, active: { type: Boolean, default: true } });
const view = ref(null);
const background = computed(() => displayPoolImage(props.pool.poolBackgroundUrl));
const amountBackground = computed(() => displayPoolImage(props.pool.amountBackgroundUrl));
const fontColor = computed(() => displayPoolColor(props.pool.fontColor));
const separatorColor = computed(() => displayPoolColor(props.pool.separatorColor));
// 保留运营配置的文字颜色，在任意配图上提供对应明暗的金额底色。
const amountBackdrop = computed(() => {
  const channels = fontColor.value.slice(1).match(/../g).map(channel => parseInt(channel, 16));
  return channels[0] * 0.299 + channels[1] * 0.587 + channels[2] * 0.114 > 128 ? '#142238f2' : '#fffffff2';
});
const clock = () => typeof performance === 'object' ? performance.now() : Date.now();
let receivedAt = clock(), timer;
const stop = () => { if (timer) clearInterval(timer); timer = undefined; };
function update() {
  try { view.value = displayPoolView(props.pool, clock() - receivedAt); }
  catch { view.value = null; }
  if (!view.value || view.value.state === 3 || view.value.state === 4) stop();
}
function start() {
  stop(); update();
  if (props.active && view.value && view.value.state !== 3 && view.value.state !== 4) timer = setInterval(update, 1000);
}
watch(() => props.pool, () => { receivedAt = clock(); start(); }, { immediate: true });
watch(() => props.active, active => { if (active) start(); else stop(); });
onBeforeUnmount(stop);
</script>

<style scoped>
.activity-display-pool { position: relative; isolation: isolate; overflow: hidden; border: 1px solid #e5e8ef; border-radius: 16px; padding: 20px; margin: 20px 0; background: #f7f9fc; }
.display-pool-background { position: absolute; inset: 0; width: 100%; height: 100%; z-index: -1; object-fit: cover; }
.display-pool-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; color: #1d2129; }
.display-pool-heading h3 { font-size: 18px; margin: 0; overflow-wrap: anywhere; padding: 6px 8px; border-radius: 8px; background: #fffffff2; }
.display-pool-heading > :last-child { flex-shrink: 0; font-size: 12px; color: #4e5969; background: #fff; border-radius: 12px; padding: 4px 8px; }
.display-pool-amount { position: relative; isolation: isolate; overflow: hidden; border-radius: 12px; margin: 16px 0 10px; padding: 20px 12px; background: #ffffffed; text-align: center; }
.display-pool-number { position: relative; display: inline-block; padding: 8px 12px; border-radius: 10px; font-size: clamp(22px, 5vw, 36px); font-weight: 700; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.display-pool-number small { font-size: 14px; color: inherit; font-weight: 400; }
.display-pool-note { display: block; padding: 4px 8px; border-radius: 6px; font-size: 12px; line-height: 1.6; color: #4e5969; background: #fffffff2; }
</style>
